import React, { useEffect } from 'react';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import {
  CheckBoxParam,
  ComboBoxParam,
  Form,
  Input,
  NumberParam,
  RequiredHelpPhrase,
  Row,
  SonForm,
  SubmitButton,
  Switch,
  TextParam,
  useForm,
} from '../../../_lib/Form2';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentSelectComboBoxForForm2 } from '../../../components/molecules/Form/components/ComboBox';

type GenericSearchListForm = {
  form: SonForm<
    | TextParam<'title'>
    | TextParam<'searchQuery'>
    | CheckBoxParam<'showTeaserInformation'>
    | CheckBoxParam<'showBreadcrumbs'>
    | CheckBoxParam<'showAttachedFiles'>
    | NumberParam<'maxResultsSize'>
    | NumberParam<'initialResultsSize'>
    | CheckBoxParam<'individualLinkEnabled'>
    | ComboBoxParam<'individualLinkPageId'>
    | TextParam<'individualLinkName'>
  >;
};

export function GenericSearchListForm({ form }: GenericSearchListForm) {
  const { t } = useSonTranslation('GenericSearchList');
  const id = useHtmlValidId();
  const { delayedRefresh } = useRefreshBlock();

  const { formProps, params, fetchState } = useForm({ form, activateLeavingGuard: true });

  useEffect(() => {
    if (fetchState.type === 'success') {
      delayedRefresh();
    }
  }, [delayedRefresh, fetchState.type]);

  const titleParam = params.getByName('title');
  const searchQueryParam = params.getByName('searchQuery');
  const showTeaserInfoParam = params.findByName('showTeaserInformation');
  const showBreadcrumbsParam = params.findByName('showBreadcrumbs');
  const showAttachedFilesParam = params.findByName('showAttachedFiles');
  const maxResultsSizeParam = params.getByName('maxResultsSize');
  const initialResultsSizeParam = params.getByName('initialResultsSize');

  const individualLinkEnabledParam = params.getByName('individualLinkEnabled');
  const individualLinkPageIdParam = params.getByName('individualLinkPageId');
  const individualLinkNameParam = params.getByName('individualLinkName');

  const [showIndividualLinkForm, setShowIndividualLinkForm] = React.useState<boolean>(
    !!individualLinkEnabledParam.checked,
  );
  const toggleShowIndividualLink = () => {
    setShowIndividualLinkForm(!showIndividualLinkForm);
  };

  return (
    <Form id={`generic-search-list-form-${id}`} {...formProps}>
      <RequiredHelpPhrase />
      <Row>
        <Input label={t('form.inputs.title.label')} param={titleParam} />
      </Row>
      <Row>
        <Input
          label={t('form.inputs.searchQuery.label')}
          helpText={t('form.inputs.searchQuery.helpText')}
          param={searchQueryParam}
        />
      </Row>
      <Row>
        <Input
          label={t('form.inputs.maxResultsSize.label')}
          helpText={t('form.inputs.maxResultsSize.helpText')}
          param={maxResultsSizeParam}
        />
      </Row>
      <Row>
        <Input
          label={t('form.inputs.initialResultsSize.label')}
          helpText={t('form.inputs.initialResultsSize.helpText')}
          param={initialResultsSizeParam}
        />
      </Row>
      {showTeaserInfoParam && (
        <Row>
          <Switch label={t('form.inputs.showTeaserInformation.label')} param={showTeaserInfoParam} />
        </Row>
      )}
      {showBreadcrumbsParam && (
        <Row>
          <Switch label={t('form.inputs.showBreadcrumbs.label')} param={showBreadcrumbsParam} />
        </Row>
      )}
      {showAttachedFilesParam && (
        <Row>
          <Switch label={t('form.inputs.showAttachedFiles.label')} param={showAttachedFilesParam} />
        </Row>
      )}
      {individualLinkEnabledParam && (
        <Row>
          <Switch
            label={t('form.inputs.individualLinkEnabled.label')}
            helpText={t('form.inputs.individualLinkEnabled.helpText')}
            param={individualLinkEnabledParam}
            onClick={toggleShowIndividualLink}
          />
        </Row>
      )}
      {showIndividualLinkForm && (
        <>
          <Row modifier="block">
            <ContentSelectComboBoxForForm2
              labelTitle={t('form.inputs.individualLinkPageId.label')}
              labelHelpText={t('form.inputs.individualLinkPageId.helpText')}
              suggestUrl={individualLinkPageIdParam.comboBoxUrl.url}
              name={individualLinkPageIdParam.name}
              selected={individualLinkPageIdParam.selected}
            />
          </Row>
          <Row modifier="block">
            <Input
              label={t('form.inputs.individualLinkName.label')}
              helpText={t('form.inputs.individualLinkName.helpText')}
              param={individualLinkNameParam}
            />
          </Row>
        </>
      )}
      <SubmitButton
        buttonLabel={t('form.submitButton.text')}
        messageSuccess={t(`form.status.success`)}
        messageSending={t(`form.status.sending`)}
        messageFail={t(`form.status.fail`)}
        customErrorTypeMessages={{
          invalidValue: t(`form.status.invalidValue`),
        }}
        fetchState={fetchState}
      />
    </Form>
  );
}
